// © Thibault Sabathier, 2026. Tous droits réservés.
// frontend/src/components/profile/ReferralOpennessCard.js
//
// « Je peux aider » (lot 3, L3a) : réglage PERSONNEL, facultatif et révocable, affiché sur sa propre fiche.
// Désactivé par défaut. N'ouvre aucun accès aux coordonnées, n'oblige à rien, ne notifie personne. L'interface propose,
// le SERVEUR décide (compte suspendu ou en suppression : 403). Mise à jour instantanée avec verrou anti double clic ;
// en cas d'échec la valeur affichée revient à l'état réel (jamais un interrupteur qui ment).

import React, { useEffect, useRef, useState } from 'react'
import PropTypes from 'prop-types'
import { useTranslation } from 'react-i18next'
import { CAlert, CCard, CCardBody, CFormSwitch, CSpinner } from '@coreui/react'
import { setReferralOpenness } from '../../utils/referralOpennessApi'

/** @param {{enabled: boolean, onChange?: (enabled: boolean) => void}} props */
const ReferralOpennessCard = ({ enabled, onChange }) => {
  const { t } = useTranslation()
  const [value, setValue] = useState(Boolean(enabled))
  const [saving, setSaving] = useState(false)
  const [failed, setFailed] = useState(false)
  const busy = useRef(false)

  // La valeur réelle (rechargée par le parent) l'emporte sur l'état local.
  useEffect(() => {
    setValue(Boolean(enabled))
  }, [enabled])

  const toggle = async (event) => {
    if (busy.current) return
    const next = event.target.checked
    busy.current = true
    setSaving(true)
    setFailed(false)
    try {
      const res = await setReferralOpenness(next)
      const confirmed =
        res && typeof res.referral_openness === 'boolean' ? res.referral_openness : next
      setValue(confirmed)
      if (onChange) onChange(confirmed)
    } catch (e) {
      setValue(Boolean(enabled))
      setFailed(true)
    } finally {
      busy.current = false
      setSaving(false)
    }
  }

  return (
    <CCard className="referral-openness-card">
      <CCardBody>
        <div className="d-flex align-items-center justify-content-between flex-wrap gap-2">
          <h6 className="mb-0 fw-semibold">{t('members:referral.card_title')}</h6>
          <div className="d-flex align-items-center gap-2">
            {saving && <CSpinner size="sm" />}
            <CFormSwitch
              id="referral-openness-toggle"
              checked={value}
              disabled={saving}
              onChange={toggle}
              label={t('members:referral.toggle')}
            />
          </div>
        </div>
        <p className="small text-medium-emphasis mt-2 mb-1">{t('members:referral.body')}</p>
        <p className="small text-medium-emphasis mb-0">
          {value ? t('members:referral.on_hint') : t('members:referral.off_hint')}{' '}
          {t('members:referral.hidden_note')}
        </p>
        {failed && (
          <CAlert color="danger" className="mt-2 mb-0 py-2">
            {t('members:referral.error')}
          </CAlert>
        )}
      </CCardBody>
    </CCard>
  )
}

ReferralOpennessCard.propTypes = {
  enabled: PropTypes.bool,
  onChange: PropTypes.func,
}

export default ReferralOpennessCard
